// utils/storageUtils.js
const STORAGE_PREFIX = 'documentCheckin_';
const CACHE_VERSION = '1.0';

// Helper function to create storage keys
const createKey = (configId, type) => {
  return `${STORAGE_PREFIX}${configId}_${type}`;
};

// Generic storage operations with error handling
const saveToStorage = (key, data) => {
  try {
    const payload = {
      data,
      timestamp: Date.now(),
      version: CACHE_VERSION
    };
    sessionStorage.setItem(key, JSON.stringify(payload));
    return true;
  } catch (error) {
    console.error('Failed to save to storage:', error);
    // Handle storage quota exceeded
    if (error.name === 'QuotaExceededError') {
      console.warn('Storage quota exceeded, clearing old data');
      clearOldCacheData();
      // Try once more
      try {
        sessionStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now(), version: CACHE_VERSION }));
        return true;
      } catch (retryError) {
        console.error('Failed to save even after clearing cache:', retryError);
      }
    }
    return false;
  }
};

const loadFromStorage = (key) => {
  try {
    const item = sessionStorage.getItem(key);
    if (!item) return null;
    
    const payload = JSON.parse(item);
    
    // Check version compatibility
    if (payload.version !== CACHE_VERSION) {
      console.warn('Cache version mismatch, clearing data');
      sessionStorage.removeItem(key);
      return null;
    }
    
    return payload.data;
  } catch (error) {
    console.error('Failed to load from storage:', error);
    return null;
  }
};

// Form data operations
export const saveFormData = (configId, formData) => {
  const key = createKey(configId, 'form');
  return saveToStorage(key, formData);
};

export const loadFormData = (configId) => {
  const key = createKey(configId, 'form');
  return loadFromStorage(key) || {};
};

export const clearFormData = (configId) => {
  const key = createKey(configId, 'form');
  try {
    sessionStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Failed to clear form data:', error);
    return false;
  }
};

// File upload cache operations
export const saveUploadedFiles = (configId, files) => {
  const key = createKey(configId, 'files');
  return saveToStorage(key, files);
};

export const loadUploadedFiles = (configId) => {
  const key = createKey(configId, 'files');
  return loadFromStorage(key) || {};
};

export const addUploadedFile = (configId, fieldId, fileData) => {
  const existingFiles = loadUploadedFiles(configId);
  
  if (!existingFiles[fieldId]) {
    existingFiles[fieldId] = [];
  }
  
  existingFiles[fieldId].push({
    ...fileData,
    uploadedAt: Date.now(),
    id: `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  });
  
  return saveUploadedFiles(configId, existingFiles);
};

export const removeUploadedFile = (configId, fieldId, fileId) => {
  const existingFiles = loadUploadedFiles(configId);
  
  if (existingFiles[fieldId]) {
    existingFiles[fieldId] = existingFiles[fieldId].filter(file => file.id !== fileId);
    if (existingFiles[fieldId].length === 0) {
      delete existingFiles[fieldId];
    }
  }
  
  return saveUploadedFiles(configId, existingFiles);
};

export const clearUploadedFiles = (configId) => {
  const key = createKey(configId, 'files');
  try {
    sessionStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Failed to clear uploaded files:', error);
    return false;
  }
};

// Session metadata
export const saveSessionMetadata = (configId, metadata) => {
  const key = createKey(configId, 'session');
  return saveToStorage(key, {
    ...metadata,
    lastActivity: Date.now(),
    sessionId: metadata.sessionId || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  });
};

export const loadSessionMetadata = (configId) => {
  const key = createKey(configId, 'session');
  return loadFromStorage(key);
};

// Progress tracking
export const saveFormProgress = (configId, progressData) => {
  const key = createKey(configId, 'progress');
  return saveToStorage(key, {
    ...progressData,
    lastSaved: Date.now()
  });
};

export const loadFormProgress = (configId) => {
  const key = createKey(configId, 'progress');
  return loadFromStorage(key);
};

// Cleanup operations
export const clearAllDataForConfig = (configId) => {
  try {
    clearFormData(configId);
    clearUploadedFiles(configId);
    sessionStorage.removeItem(createKey(configId, 'session'));
    sessionStorage.removeItem(createKey(configId, 'progress'));
    return true;
  } catch (error) {
    console.error('Failed to clear all data for config:', error);
    return false;
  }
};

export const clearOldCacheData = () => {
  try {
    const keysToRemove = [];
    
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        try {
          const item = sessionStorage.getItem(key);
          const payload = JSON.parse(item);
          
          // Remove items older than 24 hours
          const oneDay = 24 * 60 * 60 * 1000;
          if (Date.now() - payload.timestamp > oneDay) {
            keysToRemove.push(key);
          }
        } catch (parseError) {
          // Remove invalid items
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    console.log(`Cleared ${keysToRemove.length} old cache items`);
    
    return keysToRemove.length;
  } catch (error) {
    console.error('Failed to clear old cache data:', error);
    return 0;
  }
};

// Get storage info for debugging
export const getStorageInfo = () => {
  let totalSize = 0;
  let itemCount = 0;
  let checkinItems = 0;
  
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key) {
        const value = sessionStorage.getItem(key);
        totalSize += key.length + (value ? value.length : 0);
        itemCount++;
        
        if (key.startsWith(STORAGE_PREFIX)) {
          checkinItems++;
        }
      }
    }
    
    return {
      totalSize,
      itemCount,
      checkinItems,
      totalSizeKB: Math.round(totalSize / 1024),
      // Rough estimate of available space (5MB typical limit)
      estimatedUsagePercent: Math.round((totalSize / (5 * 1024 * 1024)) * 100)
    };
  } catch (error) {
    console.error('Failed to get storage info:', error);
    return null;
  }
};
